import { Box, Typography } from '@mui/material';
import { GlassColors } from '../../components/glassmorphism/GlassmorphismStyles';
import arrowUrl from '../../assets/icons/info/arrowBlock.svg';

type Step = { title: string; text: React.ReactNode };

const steps: Step[] = [
  {
    title: 'Step 1: Create your free profile',
    text: 'Enter your basic details and select the exact services you offer from our list. No need to build a complex portfolio right away—setup takes just 2 minutes!',
  },
  {
    title: 'Step 2: Get Verified',
    text: (
      <>
        Complete a quick, secure ID check to unlock your{' '}
        <strong>'Verified Pro'</strong> badge. badge. This step keeps the
        platform safe and shows high-paying clients you are a trusted expert.
      </>
    ),
  },
  {
    title: 'Step 3: Win jobs & get paid',
    text: 'Browse local requests, chat with clients, and send your quotes for free. We only deduct our flat 10% fee after the job is successfully completed.',
  },
];

export const StepsPath = ({ isDark }: { isDark: boolean }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: '24px', tablet: '32px' },
        flexDirection: 'column',
      }}
    >
      {steps.map((s, idx) => {
        const isLast = idx === steps.length - 1;
        const isEven = idx % 2 === 0; // 0,2,4.. = left; 1,3.. = right

        return (
          <Box
            key={s.title}
            sx={{
              position: 'relative',
              width: { xs: '100%', tablet: isEven ? 480 : 486 },
              alignSelf: {
                xs: 'stretch',
                sm: isEven ? 'flex-start' : 'flex-end',
              },
              borderRadius: '32px',
              background: isDark
                ? GlassColors.dark.primaryBackground
                : GlassColors.light.primaryBackground,
              boxShadow: `
                  -2.82px -2.82px 8.49px 0px #FFFFFF4D,
                  14.15px 14.15px 22.64px -5.37px #0000001A,
                  -7.54px -7.54px 8.66px 0px #FFFFFF33,
                  5px 5px 24px -11.7px #00000033
                `,
              p: '32px',
              '&::after': {
                content: '""',
                display: { xs: 'none', tablet: isLast ? 'none' : 'block' },
                position: 'absolute',
                width: 165,
                height: 100,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                pointerEvents: 'none',
                backgroundImage: `url(${arrowUrl})`,
              },

              '@media (min-width: 768px)': {
                '::after': isLast
                  ? { display: 'none' }
                  : isEven
                    ? {
                        // even (left card)
                        bottom: 0,
                        left: '100%',
                      }
                    : {
                        // odd (right card)
                        bottom: 0,
                        right: '100%',
                        transform: 'scaleX(-1)',
                      },
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 800,
                color: isDark ? GlassColors.accent : GlassColors.secondary,
                fontSize: '20px',
                lineHeight: 1.6,
                letterSpacing: '.15px',
                mb: '10px',
                textAlign: 'center',
              }}
            >
              {s.title}
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontFamily: 'Roboto, sans-serif',
                color: isDark
                  ? GlassColors.dark.textPrimary
                  : GlassColors.light.textPrimary,
                lineHeight: 1.5,
                letterSpacing: '.15px',
                textAlign: 'center',
              }}
            >
              {s.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
